const WEBSITE_NAME_TOKEN = '{{siteName}}';
const BUSINESS_NAME_TOKEN = '{{businessName}}';
const WEBSITE_LINK_TOKEN = '{{website_link primaryDomain hostingDomain siteName}}';
const LEGACY_WEBSITE_LINK_TOKEN = '{{website_link primaryDomain previewDomain hostingDomain siteName}}';
const WEBSITE_LINK_CUSTOM_PREFIX = "{{{website_link primaryDomain hostingDomain '";
const CURRENT_YEAR_TOKEN = '{{current_year}}';

const escapeDoubleQuotedHandlebarsString = (value) => String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');

const escapeSingleQuotedHandlebarsString = (value) => String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'");

const unescapeDoubleQuotedHandlebarsString = (value) => String(value || '')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\');

const unescapeSingleQuotedHandlebarsString = (value) => String(value || '')
    .replace(/\\'/g, "'")
    .replace(/\\\\/g, '\\');

const createTextControl = ({
    key,
    label,
    placeholder = '',
    helpText = '',
    defaultValue = '',
    showsWhen = null
}) => ({
    key,
    label,
    type: 'text',
    placeholder,
    helpText,
    defaultValue,
    showsWhen
});

const createSelectControl = ({
    key,
    label,
    options,
    helpText = '',
    defaultValue = '',
    showsWhen = null
}) => ({
    key,
    label,
    type: 'select',
    options,
    helpText,
    defaultValue,
    showsWhen
});

const buildDefaultConfig = (controls = []) => controls.reduce((config, control) => ({
    ...config,
    [control.key]: control.defaultValue ?? ''
}), {});

const createFallbackControl = ({helpText}) => createTextControl({
    key: 'fallback',
    label: 'Fallback Text',
    placeholder: 'Used if this value is empty',
    helpText,
    defaultValue: ''
});

const parseQuotedTokenValue = (value, quote) => {
    if (quote === "'") {
        return unescapeSingleQuotedHandlebarsString(value);
    }

    return unescapeDoubleQuotedHandlebarsString(value);
};

const buildFallbackValueToken = (path, config = {}) => {
    const fallback = String(config?.fallback || '').trim();
    if (!fallback) {
        return `{{${path}}}`;
    }

    return `{{coalesce ${path} '${escapeSingleQuotedHandlebarsString(fallback)}'}}`;
};

const parseFallbackValueToken = (path, token, defaultConfig) => {
    const rawToken = `{{${path}}}`;
    if (token === rawToken) {
        return {...defaultConfig};
    }

    const match = token.match(new RegExp(`^{{coalesce ${path} (['"])((?:\\\\.|(?!\\1).)*)\\1}}$`));
    if (!match) return null;

    return {
        ...defaultConfig,
        fallback: parseQuotedTokenValue(match[2], match[1])
    };
};

const buildWebsiteLinkToken = (config = {}) => {
    const displayMode = config?.displayMode || 'dynamic';
    const customText = String(config?.displayText || '').trim();

    if (displayMode === 'custom' && customText) {
        return `${WEBSITE_LINK_CUSTOM_PREFIX}${escapeSingleQuotedHandlebarsString(customText)}'}}}`;
    }

    return WEBSITE_LINK_TOKEN;
};

const parseWebsiteLinkToken = (token, defaultConfig) => {
    const supportedDynamicTokens = [
        WEBSITE_LINK_TOKEN,
        LEGACY_WEBSITE_LINK_TOKEN,
        WEBSITE_LINK_TOKEN.replace('{{website_link', '{{{website_link').replace('siteName}}', 'siteName}}}'),
        LEGACY_WEBSITE_LINK_TOKEN.replace('{{website_link', '{{{website_link').replace('siteName}}', 'siteName}}}')
    ];

    if (supportedDynamicTokens.includes(token)) {
        return {...defaultConfig, displayMode: 'dynamic'};
    }

    const match = token.match(/^{{{website_link primaryDomain (?:previewDomain )?hostingDomain (['"])((?:\\.|(?!\1).)*)\1}}}$/);
    if (!match) return null;

    return {
        ...defaultConfig,
        displayMode: 'custom',
        displayText: parseQuotedTokenValue(match[2], match[1])
    };
};

const createConfigurableBrick = ({
    id,
    title,
    description,
    group,
    controls = [],
    buildToken,
    parseToken
}) => {
    const defaultConfig = buildDefaultConfig(controls);

    return {
        id,
        title,
        description,
        group,
        insertBehavior: 'insert-default',
        defaultConfig,
        configFields: controls,
        getLabel: () => title,
        matchToken: (candidate) => parseToken(candidate, defaultConfig)
            ? {
                brickId: id,
                label: title,
                configurable: controls.length > 0
            }
            : null,
        parseToken: (candidate) => parseToken(candidate, defaultConfig),
        buildToken: (config = {}) => buildToken({...defaultConfig, ...config})
    };
};

const websiteGlobalBricks = [
    createConfigurableBrick({
        id: 'website-site-name',
        title: 'Website Name',
        description: 'The public website name.',
        group: 'Website',
        controls: [
            createFallbackControl({
                helpText: "Use this if the website name isn't available."
            })
        ],
        buildToken: (config) => buildFallbackValueToken('siteName', config),
        parseToken: (token, defaultConfig) => parseFallbackValueToken('siteName', token, defaultConfig)
    }),
    createConfigurableBrick({
        id: 'website-business-name',
        title: 'Business Name',
        description: 'The business name associated with the website.',
        group: 'Business',
        controls: [
            createFallbackControl({
                helpText: "Use this if the business name isn't available."
            })
        ],
        buildToken: (config) => buildFallbackValueToken('businessName', config),
        parseToken: (token, defaultConfig) => parseFallbackValueToken('businessName', token, defaultConfig)
    }),
    createConfigurableBrick({
        id: 'website-site-link',
        title: 'Website Link',
        description: 'A link to the main website domain using the website name as the label.',
        group: 'Website',
        controls: [
            createSelectControl({
                key: 'displayMode',
                label: 'Display As',
                defaultValue: 'dynamic',
                options: [
                    {label: 'Website name', value: 'dynamic'},
                    {label: 'Custom text', value: 'custom'}
                ],
                helpText: 'Choose whether the link should show the website name or your own text.'
            }),
            createTextControl({
                key: 'displayText',
                label: 'Custom Text',
                placeholder: 'Optional link text',
                helpText: 'Shown when you choose Custom text.',
                defaultValue: '',
                showsWhen: (config) => config?.displayMode === 'custom'
            })
        ],
        buildToken: buildWebsiteLinkToken,
        parseToken: parseWebsiteLinkToken
    }),
    createConfigurableBrick({
        id: 'website-current-year',
        title: 'Current Year',
        description: 'The current year at render time.',
        group: 'Website',
        buildToken: () => CURRENT_YEAR_TOKEN,
        parseToken: (token, defaultConfig) => token === CURRENT_YEAR_TOKEN ? {...defaultConfig} : null
    })
];

export default websiteGlobalBricks;
